<script lang="ts" setup>
import { computed } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { useDisplayName } from '@Shared';
import usePhoneNumber from '@Use/usePhoneNumber';
import CollectAccountInfo from '@Components/CollectAccountInfo.vue';
import { usePdfDownload } from '@Use/usePdfDownload';

withDefaults(defineProps<{
    showTitle?: boolean;
}>(), {
    showTitle: true,
});

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestDetailsStore = useRequestDetailsStore();
const requestDetail = computed(() => requestDetailsStore.requestDetails);

const { displayName: companyName } = useDisplayName(
    () => companyStore.company,
    () => locationStore.location,
);

const { rawPhone, formattedPhone: companyPhone } = usePhoneNumber(
    () => companyStore.company,
    () => locationStore.location,
);

const { isPdfLoading, pdfError, downloadPdf } = usePdfDownload();
</script>

<template>
    <app-form-content-layout>
        <h1
            v-if="showTitle"
            class="collect-complete__headline">
            Thank you!
        </h1>

        <p class="collect-complete__description">
            These account details have been shared securely with
            <strong>{{ companyName }}</strong>.
        </p>

        <collect-account-info
            v-if="requestDetail?.wiringInstructions"
            :wiring-instructions="requestDetail.wiringInstructions"
            toggle-data-id="collect-complete-toggle"
            collapse-initial-state="closed"
        />

        <app-button
            expanded
            size="large"
            data-id="collect-complete-download"
            :is-disabled="isPdfLoading"
            :is-loading="isPdfLoading"
            @onClick="downloadPdf">
            Download PDF
        </app-button>

        <p v-if="pdfError" role="alert" class="collect-complete__error">
            {{ pdfError }}
        </p>

        <p class="collect-complete__contact">
            If you have additional questions please call:
            <template v-if="companyName || companyPhone">
                <br />
                <a v-if="companyPhone" :href="`tel:${rawPhone}`"
                   class="collect-complete__contact-link">
                    {{ companyName }} - {{ companyPhone }}
                </a>
                <span v-else class="collect-complete__contact-link">
                    {{ companyName }}
                </span>
            </template>
        </p>
    </app-form-content-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.collect-complete {
    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__contact {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        text-align: center;
        margin: 0;
    }

    &__contact-link {
        color: $theme-primary;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        text-align: center;
        margin: 0;
    }
}
</style>
